import { computed, ref } from "vue";
import { defineStore } from "pinia";
import type { RouteLocationRaw } from "vue-router";
import { keycloakSession } from "@/auth/keycloak-session";
import { useIdentityStore } from "@/stores/identity";
import { usePropertiesStore } from "@/stores/properties";

/**
 * A "workspace" is the focused set of pages and dashboard sections a user can
 * operate in. A single account can span more than one (e.g. someone who both
 * hosts a property and cleans), so the header exposes an explicit switcher and
 * the chosen workspace is remembered across sessions.
 */
export type Workspace = "owner" | "cleaner" | "dry-cleaner";

export const WORKSPACE_LABELS: Record<Workspace, string> = {
  owner: "Property owner",
  cleaner: "Cleaner",
  "dry-cleaner": "Dry cleaner",
};

export interface WorkspaceNavItem {
  readonly label: string;
  readonly to: RouteLocationRaw;
}

/**
 * The navigation each workspace shows, declared next to the role mapping so the
 * two cannot drift: a workspace only appears in the header when the account
 * actually holds the roles behind it (see `available`), and this is the only
 * place a workspace's links are defined.
 */
export const WORKSPACE_NAV: Record<Workspace, readonly WorkspaceNavItem[]> = {
  owner: [
    { label: "Dashboard", to: "/dashboard" },
    { label: "Properties", to: "/properties" },
  ],
  cleaner: [
    { label: "Dashboard", to: "/dashboard" },
    { label: "Find work", to: "/market/jobs" },
    { label: "Invitations", to: "/invitations" },
    { label: "My jobs", to: "/bookings" },
  ],
  "dry-cleaner": [
    { label: "Dashboard", to: "/dashboard" },
    { label: "Laundry provider", to: { name: "laundry-provider-setup" } },
    { label: "My deliveries", to: { name: "linen-orders" } },
  ],
};

const WORKSPACE_ORDER: readonly Workspace[] = ["owner", "cleaner", "dry-cleaner"];
const STORAGE_KEY = "washbox.workspace";

function readStoredWorkspace(): Workspace | null {
  try {
    const value = window.localStorage.getItem(STORAGE_KEY);
    return value === "owner" || value === "cleaner" || value === "dry-cleaner" ? value : null;
  } catch {
    return null;
  }
}

export const useWorkspaceStore = defineStore("workspace", () => {
  const identity = useIdentityStore();
  const properties = usePropertiesStore();

  const stored = ref<Workspace | null>(readStoredWorkspace());
  // Whether we have checked the owner workspace's availability for a provider.
  const ownerChecked = ref(false);

  const providerWorkspaces = computed<Workspace[]>(() => {
    const list: Workspace[] = [];
    if (identity.isCleaner) list.push("cleaner");
    if (identity.isLaundryProvider) list.push("dry-cleaner");
    return list;
  });

  /**
   * Dry cleaners are single-service businesses: they never host property or
   * find cleaners, so they never get the owner workspace (and thus never a
   * switcher). A cleaner gets the owner workspace only once we confirm they
   * actually own property, so a single-purpose cleaner sees no pointless
   * switcher either. A plain customer always has it.
   */
  const ownerAvailable = computed(() => {
    if (identity.isLaundryProvider) return false;
    if (identity.isCleaner) return ownerChecked.value && properties.properties.length > 0;
    return identity.isCustomer;
  });

  const available = computed<Workspace[]>(() =>
    WORKSPACE_ORDER.filter((workspace) =>
      workspace === "owner" ? ownerAvailable.value : providerWorkspaces.value.includes(workspace),
    ),
  );

  const canSwitch = computed(() => available.value.length > 1);

  const active = computed<Workspace>(() => {
    const list = available.value;
    if (stored.value !== null && list.includes(stored.value)) return stored.value;
    const primary = identity.profileType;
    if (list.includes(primary)) return primary;
    return list[0] ?? "owner";
  });

  function select(workspace: Workspace): void {
    if (!available.value.includes(workspace)) return;
    stored.value = workspace;
    try {
      window.localStorage.setItem(STORAGE_KEY, workspace);
    } catch {
      // A blocked storage API only costs the persisted preference.
    }
  }

  async function ensureOwnerChecked(): Promise<void> {
    if (ownerChecked.value) return;
    if (!identity.isCleaner || identity.isLaundryProvider) {
      // Only a cleaner can additionally own property; a dry cleaner never can,
      // and a plain owner already has the workspace with no lookup needed.
      ownerChecked.value = true;
      return;
    }
    try {
      await properties.load();
    } catch {
      // Best effort: without the check a provider simply keeps their work view.
    }
    ownerChecked.value = true;
  }

  function reset(): void {
    ownerChecked.value = false;
    stored.value = null;
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch {
      // A blocked storage API only costs the persisted preference.
    }
  }

  keycloakSession.subscribe((authenticated) => {
    if (!authenticated) reset();
  });

  return { available, canSwitch, active, select, ensureOwnerChecked, reset };
});
